import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Import Enterprise Persona components
import Login from './components/Login';
import Register from './components/Register';
import Settings from './components/Settings';
import PromptBank from './components/PromptBank';
import Security from './components/Security';
import EnterpriseDashboard from './components/EnterpriseDashboard';
import UserDashboard from './components/UserDashboard';
import AdminDashboard from './components/AdminDashboard';
import SSOLogin from './components/SSOLogin';

// Import pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PricingPage from './components/PricingPage';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading Enterprise Persona...</div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Public Route component
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" /> : children;
};

// Landing page component
const Landing = () => {
  const [currentStats, setCurrentStats] = useState({
    enterprises: 0,
    violations: 0,
    compliance: 0
  });

  const [showComingSoon, setShowComingSoon] = useState(true);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    // Check if user has already entered password
    const hasAccess = localStorage.getItem('enterprise-persona-dev-access');
    if (hasAccess === 'true') {
      setShowComingSoon(false);
    }

    // Animate stats
    const animateStats = () => {
      setCurrentStats({
        enterprises: 150,
        violations: 1247,
        compliance: 99.8
      });
    };

    const timer = setTimeout(animateStats, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
    setPasswordError(''); // Clear error when user starts typing
  }, []);

  const handlePasswordSubmit = useCallback((e) => {
    e.preventDefault();
    if (password === 'enterprise2024') {
      setShowComingSoon(false);
      localStorage.setItem('enterprise-persona-dev-access', 'true');
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  }, [password]);

  const FloatingOrbs = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="absolute animate-enterprise-pulse"
          style={{
            width: `${30 + Math.random() * 40}px`,
            height: `${30 + Math.random() * 40}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            borderRadius: '6px',
            background: 'rgba(59, 130, 246, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
            animationDelay: `${i * 2}s`,
            animationDuration: `${8 + Math.random() * 4}s`
          }}
        />
      ))}
      {[...Array(15)].map((_, i) => (
        <div
          key={`dot-${i}`}
          className="absolute"
          style={{
            width: '3px',
            height: '3px',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            borderRadius: '50%',
            background: 'rgba(59, 130, 246, 0.15)',
            animationDelay: `${i * 1.5}s`,
            animationDuration: `${12 + Math.random() * 6}s`,
            animation: 'subtle-float 18s ease-in-out infinite'
          }}
        />
      ))}
    </div>
  );

  // Coming Soon Overlay Component
  const ComingSoonOverlay = () => (
    <div className="fixed inset-0 bg-black bg-opacity-95 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="text-center max-w-2xl mx-auto px-6">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div 
              className="w-16 h-16 flex items-center justify-center relative"
              style={{
                background: 'transparent',
                border: '2px solid rgba(59, 130, 246, 0.6)',
                clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'
              }}
            >
              <div className="w-3 h-3 bg-blue-500 rounded-full relative z-10"></div>
            </div>
          </div>
        </div>

        {/* Coming Soon Text */}
        <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
          <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent">
            Coming Soon
          </span>
        </h1>
        
        <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
          We're building something amazing for enterprise AI compliance monitoring.
        </p>

        {/* Password Form */}
        <div className="glass-morphism rounded-2xl p-8 max-w-md mx-auto">
          <h3 className="text-lg font-semibold mb-4 text-gray-200">Developer Access</h3>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <input
                key="password-input"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                placeholder="Enter developer password"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoComplete="off"
                required
              />
              {passwordError && (
                <p className="text-red-400 text-sm mt-2">{passwordError}</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Access Site
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-12 text-gray-400 text-sm">
          <p>© 2024 Enterprise Persona. All rights reserved.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <FloatingOrbs />
      
      {/* Coming Soon Overlay */}
      {showComingSoon && <ComingSoonOverlay />}
      <style>{`
        @keyframes enterprise-pulse {
          0% { 
            transform: scale(1);
            background: rgba(59, 130, 246, 0.08);
            border-color: rgba(59, 130, 246, 0.12);
          }
          50% { 
            transform: scale(1.15);
            background: rgba(59, 130, 246, 0.12);
            border-color: rgba(59, 130, 246, 0.18);
          }
          100% { 
            transform: scale(1);
            background: rgba(59, 130, 246, 0.08);
            border-color: rgba(59, 130, 246, 0.12);
          }
        }
        @keyframes subtle-float {
          0%, 100% { 
            transform: translateY(0px) translateX(0px);
            opacity: 0.1;
          }
          33% { 
            transform: translateY(-15px) translateX(10px);
            opacity: 0.25;
          }
          66% { 
            transform: translateY(5px) translateX(-5px);
            opacity: 0.15;
          }
        }
        .animate-enterprise-pulse {
          animation: enterprise-pulse 8s ease-in-out infinite;
        }
        .gradient-text {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glass-morphism {
          background: rgba(15, 23, 42, 0.7);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(148, 163, 184, 0.08);
        }
        .hover-lift {
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .hover-lift:hover {
          transform: translateY(-6px) scale(1.01);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
        }
        .neural-network {
          background-image: 
            radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.04) 1.5px, transparent 1.5px),
            radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.06) 2px, transparent 2px);
          background-size: 160px 160px, 100px 100px;
          background-position: 0 0, 50px 50px;
          animation: neural-pulse 16s ease-in-out infinite;
        }
        @keyframes neural-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.6; }
        }
        .text-glow {
          text-shadow: 0 0 15px rgba(59, 130, 246, 0.3);
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-morphism">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="relative">
                {/* Blue Octagon Logo - enterprise style */}
                <div 
                  className="w-10 h-10 flex items-center justify-center relative"
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(59, 130, 246, 0.4)',
                    clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'
                  }}
                >
                  {/* Center dot */}
                  <div className="w-2 h-2 bg-blue-500 rounded-full relative z-10"></div>
                </div>
              </div>
              <span className="text-xl font-bold">Enterprise Persona</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="hover:text-blue-400 transition-colors">Features</a>
              <a href="#demo" className="hover:text-blue-400 transition-colors">Demo</a>
              <a href="/pricing" className="hover:text-blue-400 transition-colors">Pricing</a>
              <a href="/sso-login" className="hover:text-blue-400 transition-colors">SSO Login</a>
              <a href="/login" className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors text-white no-underline">
                Login
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4">
        <div className="neural-network absolute inset-0"></div>
        <div className="relative z-10 text-center max-w-6xl mx-auto">
          {/* Live Stats */}
          <div className="inline-flex items-center space-x-6 glass-morphism rounded-full px-6 py-3 mb-8 transition-all duration-700 ease-out">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-300">Enterprise Protection</span>
            </div>
            <div className="text-sm transition-all duration-500">
              <span className="text-blue-400 font-semibold">{currentStats.enterprises}+</span> enterprises
            </div>
            <div className="text-sm transition-all duration-500">
              <span className="text-purple-400 font-semibold">{currentStats.violations}</span> violations detected
            </div>
            <div className="text-sm transition-all duration-500">
              <span className="text-green-400 font-semibold">{currentStats.compliance}%</span> compliance rate
            </div>
          </div>
          
          <h1 className="text-6xl md:text-8xl font-black mb-6 leading-tight">
            <span className="text-glow">AI Compliance</span>
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent">
              Enterprise Shield
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl mb-12 text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Protect your enterprise with <span className="text-blue-400 font-semibold">AI-powered compliance monitoring</span> + <span className="text-green-400 font-semibold">real-time violation detection</span> + <span className="text-purple-400 font-semibold">comprehensive audit trails</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <a href="/register" className="group relative px-8 py-4 bg-blue-600 hover:bg-blue-700 rounded-xl text-lg font-semibold transition-all duration-500 hover-lift overflow-hidden text-white no-underline">
              <span className="relative z-10">🛡️ Protect Your Enterprise</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </a>
            <a href="/sso-login" className="px-8 py-4 glass-morphism rounded-xl text-lg font-medium hover-lift">
              🔐 Enterprise SSO Login
            </a>
            <a href="/pricing" className="px-8 py-4 glass-morphism rounded-xl text-lg font-medium hover-lift">
              💼 View Enterprise Plans
            </a>
            <a href="#demo" className="group relative px-8 py-4 bg-green-600 hover:bg-green-700 rounded-xl text-lg font-semibold transition-all duration-500 hover-lift overflow-hidden text-white no-underline">
              <span className="relative z-10">🎬 Watch Demo</span>
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </a>
          </div>
          
          {/* Platform Icons */}
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            {['🤖 ChatGPT', '🧠 Claude', '✨ Gemini', '🚀 Copilot', '🔍 Perplexity', '💼 Enterprise AI'].map((platform, i) => (
              <div key={i} className="text-sm hover:opacity-100 transition-opacity duration-500 cursor-pointer">
                {platform}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Bridge Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Your <span className="gradient-text">Compliance Shield</span>
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Enterprise Persona is the enterprise-grade solution that protects your AI interactions. 
            Monitor compliance across all AI platforms, detect violations in real-time, and maintain audit trails for regulatory requirements.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="glass-morphism rounded-2xl p-6 hover-lift border-2 border-blue-500/30">
              <div className="text-4xl mb-4">🔒</div>
              <h3 className="text-xl font-bold mb-3">Real-time Monitoring</h3>
              <p className="text-gray-400 text-sm">
                Instantly detect PII, trade secrets, and policy violations across all AI platforms with zero latency.
              </p>
            </div>
            
            <div className="glass-morphism rounded-2xl p-6 hover-lift border-2 border-purple-500/30">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-bold mb-3">Compliance Analytics</h3>
              <p className="text-gray-400 text-sm">
                Comprehensive reporting and analytics for executives, compliance officers, and auditors.
              </p>
            </div>
            
            <div className="glass-morphism rounded-2xl p-6 hover-lift border-2 border-green-500/30">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-bold mb-3">Instant Alerts</h3>
              <p className="text-gray-400 text-sm">
                Get notified immediately when violations are detected, with configurable escalation paths.
              </p>
            </div>
            
            <div className="glass-morphism rounded-2xl p-6 hover-lift border-2 border-cyan-500/30">
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="text-xl font-bold mb-3">Enterprise Ready</h3>
              <p className="text-gray-400 text-sm">
                SOC 2, GDPR, HIPAA compliant with enterprise SSO, role-based access, and audit logs.
              </p>
            </div>
          </div>
          
          <div className="glass-morphism rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold mb-6">Stop Compliance Violations Before They Happen</h3>
            <p className="text-lg text-gray-300 mb-6">
              <strong>Protect once, monitor everywhere.</strong> Your enterprise compliance becomes your universal AI safety net, 
              ensuring every interaction meets regulatory requirements, regardless of which AI platform your team uses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/register" className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl text-lg font-semibold hover-lift overflow-hidden text-white no-underline">
                <span className="relative z-10">🛡️ Deploy Your Shield</span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
              <a href="/pricing" className="px-8 py-4 glass-morphism rounded-xl text-lg font-medium hover-lift">
                💼 Enterprise Pricing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Features Section */}
      <section id="features" className="py-20 px-4 relative overflow-hidden">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Enterprise-Grade <span className="gradient-text">Protection</span>
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Built for enterprises that demand the highest level of security, compliance, and reliability.
            Protect your organization with industry-leading AI compliance monitoring.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="glass-morphism rounded-2xl p-8 hover-lift border-2 border-blue-500/30">
              <div className="text-4xl font-bold text-blue-400 mb-2">Real-time</div>
              <div className="text-gray-300 mb-4">Violation Detection</div>
              <div className="text-sm text-gray-400">
                Instant detection of PII, trade secrets, and policy violations
              </div>
            </div>
            <div className="glass-morphism rounded-2xl p-8 hover-lift border-2 border-green-500/30">
              <div className="text-4xl font-bold text-green-400 mb-2">99.8%</div>
              <div className="text-gray-300 mb-4">Detection Accuracy</div>
              <div className="text-sm text-gray-400">
                Industry-leading accuracy with minimal false positives
              </div>
            </div>
            <div className="glass-morphism rounded-2xl p-8 hover-lift border-2 border-purple-500/30">
              <div className="text-4xl font-bold text-purple-400 mb-2">24/7</div>
              <div className="text-gray-300 mb-4">Monitoring</div>
              <div className="text-sm text-gray-400">
                Continuous monitoring across all AI platforms and time zones
              </div>
            </div>
          </div>
          
          {/* Compliance Factors */}
          <div className="glass-morphism rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold mb-6">What Makes Enterprise Persona Enterprise-Ready?</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="text-center">
                <div className="text-3xl mb-2">🔐</div>
                <h4 className="font-semibold mb-2">SOC 2 Certified</h4>
                <p className="text-sm text-gray-400">Enterprise-grade security compliance</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">🌍</div>
                <h4 className="font-semibold mb-2">GDPR Compliant</h4>
                <p className="text-sm text-gray-400">Full data privacy protection</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">🏥</div>
                <h4 className="font-semibold mb-2">HIPAA Ready</h4>
                <p className="text-sm text-gray-400">Healthcare compliance support</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">👥</div>
                <h4 className="font-semibold mb-2">SSO Integration</h4>
                <p className="text-sm text-gray-400">Enterprise authentication</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">📋</div>
                <h4 className="font-semibold mb-2">Audit Trails</h4>
                <p className="text-sm text-gray-400">Complete compliance records</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="max-w-6xl mx-auto text-center">
          <div className="glass-morphism rounded-2xl p-12">
            <h2 className="text-4xl font-bold mb-6">Ready to Protect Your Enterprise?</h2>
            <p className="text-gray-300 mb-8 text-lg">Join hundreds of enterprises already using Enterprise Persona for AI compliance monitoring</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/register" className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 hover-lift">
                🚀 Start Free Trial
              </a>
              <a href="/pricing" className="bg-green-600 hover:bg-green-700 px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 hover-lift">
                💼 View Enterprise Plans
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-800">
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-gray-400 mb-4">
            © 2024 Enterprise Persona. All rights reserved. Enterprise-grade AI compliance monitoring.
          </div>
        </div>
      </footer>
    </div>
  );
};

// Main App component
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
          
          <Routes>
            {/* Landing page as the home route */}
            <Route path="/" element={<Landing />} />
            
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/sso-login" 
              element={
                <PublicRoute>
                  <SSOLogin />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />
            
            {/* Protected Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <EnterpriseDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/user-dashboard" 
              element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin-dashboard" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/prompts" 
              element={
                <ProtectedRoute>
                  <PromptBank />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/security" 
              element={
                <ProtectedRoute>
                  <Security />
                </ProtectedRoute>
              } 
            />

            {/* Additional Public Pages */}
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            
            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
